import React, { useEffect, useState } from "react";
import { useLocation } from "react-router-dom";
import { renewalPayment } from "../../../../../Api/apiUtils";
import { Button, Card, Table, Loader, EmptyState } from "../../../../../ui";
import { Dialog, Callout, Actions } from "../ui-pending";
import { toast } from "react-toastify";
import CustomPayment from './custom-payment';
import OfflineCustomPayment from './offline-custom-payment';
import OtherPayment from './other-payment';
import ExternalExam from './externalExam';
import axios from "axios";
import { RENEWAL_PAYMENT } from "../../../../../Api/api-constant";
import { downloadInvoice, hasInvoice } from "../../../../../utils/invoiceDownload";
import "./payment-panels.scss";

interface PaymentDetailsProps {
  studentId: string;
}

const ExternalPayment: React.FC<any> = ({ studentId, profileData, isOffline }) => {
  const location = useLocation();
  const isOfflineStudent = isOffline || new URLSearchParams(location.search).get('student') === 'offline';
  const [paymentData, setPaymentData] = useState<any[] | null>(null);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const [paymentModal, setPaymentModal] = useState<boolean>(false); 
  const [freePaymentModal, setFreePaymentModal] = useState<boolean>(false);
  const [isOtherPaymentModalVisible, setIsOtherPaymentModalVisible] = useState<boolean>(false);
  const [isExternalExamModalVisible, setIsExternalExamModalVisible] = useState<boolean>(false);
  const [otherPaymentlist, setOtherPaymentlist] = useState<any[]>([]);
  const [invoiceLoading, setInvoiceLoading] = useState<{ [key: string]: boolean }>({});

  useEffect(() => {
    fetchPaymentDetails();
    getOtherPaymentDetails();
  }, [studentId]);

  // Get cookie helper function
  const getCookie = (name: string): string | null => {
    const value = `; ${document.cookie}`;
    const parts = value.split(`; ${name}=`);
    if (parts.length === 2) return parts.pop()?.split(';').shift() || null;
    return null;
  };

  // Get token
  const getToken = () => {
    const jwtToken = getCookie("jwtToken");
    const authToken = localStorage.getItem('authToken');
    return {
      authorization: jwtToken || authToken || ''
    };
  };

  // Get other payment list
  const getOtherPayment = async () => {
    const token = getToken();
    return axios.get(`${RENEWAL_PAYMENT}/api/payment/other-payment/${studentId}`,
      { headers: { "Authorization": token['authorization'] } });
  };

  const getOtherPaymentDetails = () => {
    getOtherPayment().then((result) => {
      if(result.data?.length){
        setOtherPaymentlist(result.data);
      } else {
        setOtherPaymentlist([]);
      }
    }).catch((error) => {
      console.error("Error fetching other payments:", error);
      setOtherPaymentlist([]);
    });
  };

  const fetchPaymentDetails = async () => {
    try {
      const response = await renewalPayment(studentId);
      console.log("API Response:", response); // ✅ Debugging
      setPaymentData(response);
    } catch (err) {
      setError("Failed to fetch payment details.");
    } finally {
      setLoading(false);
    }
  };

  const handleDownloadInvoice = async (payment: any, key: string) => {
    setInvoiceLoading(prev => ({ ...prev, [key]: true }));
    await downloadInvoice(payment);
    setInvoiceLoading(prev => ({ ...prev, [key]: false }));
  };

  // ✅ Function to format date (11-Dec-2024 format)
  const formatDate = (dateString: string) => {
    if (!dateString) return "N/A";
    const options: Intl.DateTimeFormatOptions = { day: "2-digit", month: "short", year: "numeric" };
    return new Date(dateString).toLocaleDateString("en-GB", options);
  };

  const togglePaymentModal = () => setPaymentModal(!paymentModal);
  const toggleFreePaymentModal = () => setFreePaymentModal(!freePaymentModal);
  const toggleOtherPaymentModal = () => setIsOtherPaymentModalVisible(!isOtherPaymentModalVisible);
  const toggleExternalExamModal = () => setIsExternalExamModalVisible(!isExternalExamModalVisible);

  return (
    <div className="ds-stack payment-panel">
      <Dialog size="xl" isOpen={paymentModal} onClose={togglePaymentModal} title="Capture manual payment">
        {isOfflineStudent ? (
          <OfflineCustomPayment isExternal={true} profileData={profileData} studentId={studentId} saveCallBack={() => {fetchPaymentDetails(); setPaymentModal(false)}} />
        ) : (
          <CustomPayment isExternal={true} profileData={profileData} studentId={studentId} saveCallBack={() => {fetchPaymentDetails(); setPaymentModal(false)}} />
        )}
      </Dialog>
      <Dialog size="xl" isOpen={freePaymentModal} onClose={toggleFreePaymentModal} title="Add free class">
        {isOfflineStudent ? (
          <OfflineCustomPayment isFree={true} isExternal={true} profileData={profileData} studentId={studentId} saveCallBack={() => {fetchPaymentDetails(); setFreePaymentModal(false)}} />
        ) : (
          <CustomPayment isFree={true} isExternal={true} profileData={profileData} studentId={studentId} saveCallBack={() => {fetchPaymentDetails(); setFreePaymentModal(false)}} />
        )}
      </Dialog>

      {/* Other Payment Modal */}
      <OtherPayment
        isOpen={isOtherPaymentModalVisible}
        toggle={toggleOtherPaymentModal}
        studentId={studentId}
        profileData={profileData}
        isOffline={isOffline}
        onSuccess={() => {
          fetchPaymentDetails();
          getOtherPaymentDetails();
        }}
      />

      <ExternalExam
        isOpen={isExternalExamModalVisible}
        toggle={toggleExternalExamModal}
        studentId={studentId}
        profileData={profileData}
      />

      <Card
        title="External payment details"
        subtitle="Payments recorded outside the payment link flow."
        flush
        actions={
          <Actions>
            <Button variant="secondary" onClick={toggleFreePaymentModal}>
              Add Free Class
            </Button>
            <Button variant="secondary" onClick={toggleOtherPaymentModal}>
              Record Other Payment
            </Button>
            <Button variant="primary" onClick={togglePaymentModal}>
              Record External Payment
            </Button>
            {/* <Button onClick={toggleExternalExamModal}>
              Add External Exam
            </Button> */}
          </Actions>
        }
      >
        {loading ? (
          <Loader label="Loading payments…" />
        ) : error ? (
          <Callout tone="danger">{error}</Callout>
        ) : paymentData?.length ? (
          <Table caption="External payments">
            <thead>
              <tr>
                <th>Date Created</th>
                <th>Course Name</th>
                <th className="ds-num">No. of Classes</th>
                <th className="ds-num">Amount</th>
                <th className="ds-num">Discount</th>
                <th>Payment</th>
                <th>Reason</th>
                <th>Created By</th>
                <th>Invoice</th>
              </tr>
            </thead>
            <tbody>
              {paymentData.map((payment, index) =>
                payment.courseList.map((course:any, courseIndex:any) => (
                  <tr key={`${index}-${courseIndex}`}>
                    <td>{courseIndex === 0 ? formatDate(payment.invoice_date) : ""}</td>
                    <td>{course.courseName} - {course.level} {course.isFree ? "- Free" : ""}</td>
                    <td className="ds-num">{course.classesToPurchase}</td>
                    <td className="ds-num payment-panel__nowrap">{payment?.currency + " "}{course.isFree ? 0 : course.price || 0}</td>
                    <td className="ds-num payment-panel__nowrap">{payment?.currency + " "}{course.discountedPrice || 0}</td>
                    <td>{courseIndex === 0 ? payment.paymentMode : ""}</td>
                    <td>{courseIndex === 0 ? payment.freeClassReason : ""}</td>
                    <td>{courseIndex === 0 ? payment.createdBy : "-"}</td>
                    <td>
                      {courseIndex === 0 ? (hasInvoice(payment) ? (
                        <Button
                          title="Download Invoice"
                          variant="ghost"
                          size="sm"
                          className="payment-panel__nowrap"
                          onClick={() => handleDownloadInvoice(payment, `${index}`)}
                          loading={invoiceLoading[`${index}`]}
                        >
                          {invoiceLoading[`${index}`] ? "Downloading..." : "Download Invoice"}
                        </Button>
                      ) : "-") : ""}
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </Table>
        ) : (
          <EmptyState
            title="No external payments"
            description="Use Record External Payment or Add Free Class to add one."
          />
        )}
      </Card>

      {/* Other Payments List */}
      {otherPaymentlist && otherPaymentlist.length ? (
        <Card title="Other payments" flush>
          <Table caption="Other payments" id="tab_logic_other">
            <thead>
              <tr>
                <th>Date</th>
                <th>Payment Type</th>
                <th className="ds-num">Amount</th>
                <th className="ds-num">Discount</th>
                <th className="ds-num">Net Amount</th>
                <th>Invoice</th>
                <th>Created By</th>
              </tr>
            </thead>
            <tbody>
              {otherPaymentlist.map((item, idx) => (
                <tr key={idx}>
                  <td>{item.createdAt ? new Date(item.createdAt).toLocaleString() : "N/A"}</td>
                  <td>
                    {item.otherPayment == "sale_of_instrument" ? "Sale of Musical Instrument" : 
                     item.otherPayment == "course_extension_fee" ? "Course Extension Fee" :
                     item.otherPayment == "summer_camp" ? "Summer Camp" :
                     item.otherPayment == "asm_fees_online" ? "ASM Fees - Online" :
                     item.otherPayment}
                  </td>
                  <td className="ds-num">{item.currency === 'INR' ? '₹ ' : '$ '}{item.amount}</td>
                  <td className="ds-num">{item.discountPercent || 0}%</td>
                  <td className="ds-num">{item.currency === 'INR' ? '₹ ' : '$ '}{(item.amount - (item.amount * (item.discountPercent || 0) / 100)).toFixed(2)}</td>
                  <td>
                    {item.invoice_result?.invoice_url || item.invoiceLink || item.invoice_url ? (
                      <a
                        href={item.invoice_result?.invoice_url || item.invoiceLink || item.invoice_url}
                        target="_blank"
                        rel="noopener noreferrer"
                      >
                        View
                      </a>
                    ) : (
                      "-"
                    )}
                  </td>
                  <td>{item.createdBy}</td>
                </tr>
              ))}
            </tbody>
          </Table>
        </Card>
      ) : null}
    </div>
  );
};

export default ExternalPayment;
